import { useState } from 'react';
import { createFileRoute, useNavigate, Link } from '@tanstack/react-router';
import { ProjectForm } from '../../components/projects';
import {
  createProject,
  ApiError,
  type CreateProjectInput,
  type UpdateProjectInput,
} from '../../lib/apiClient';
import '@tylertech/forge/icon';
import '@tylertech/forge/banner';
import './new.css';

export const Route = createFileRoute('/projects/new')({
  component: CreateProjectPage,
});

export { CreateProjectPage };

function CreateProjectPage(): JSX.Element {
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const IconElement = 'forge-icon' as React.ElementType;
  const BannerElement = 'forge-banner' as React.ElementType;

  const handleSubmit = async (data: CreateProjectInput | UpdateProjectInput): Promise<void> => {
    setIsSubmitting(true);
    setError(null);
    try {
      const project = await createProject(data as CreateProjectInput);
      navigate({ to: '/projects/$projectId', params: { projectId: project.entityId } });
    } catch (err) {
      setError(
        err instanceof ApiError ? err.message : 'An unexpected error occurred. Please try again.'
      );
      setIsSubmitting(false);
    }
  };

  const handleCancel = (): void => {
    navigate({ to: '/projects', search: { page: 1, pageSize: 25 } });
  };

  return (
    <div className="create-project-page">
      <div className="breadcrumb">
        <Link to="/projects" search={{ page: 1, pageSize: 25 }} className="breadcrumb-link">
          <IconElement name="arrow_back" aria-hidden="true" />
          <span>Back to Projects</span>
        </Link>
      </div>

      <div className="page-header page-header--stacked">
        <h1 className="forge-typography--headline4">Create Project</h1>
        <p className="forge-typography--body2 page-header-subtitle">
          Add a new engagement project to EA Modeling
        </p>
      </div>

      {error && (
        <BannerElement theme="error" dismissed={false} className="error-banner" role="alert">
          <div className="banner-content">
            <IconElement name="error" aria-hidden="true" />
            <span>{error}</span>
          </div>
        </BannerElement>
      )}

      <ProjectForm
        mode="create"
        onSubmit={handleSubmit}
        onCancel={handleCancel}
        isSubmitting={isSubmitting}
      />
    </div>
  );
}
